import clsx from 'clsx';
import { useFormik } from 'formik';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, KeenIcon } from '@/components';
import { useResetPasswordMutation } from '@/services/AuthService';
import { useSnackbar } from 'notistack';
import { resetPasswordChangeSchema } from '@/utils/validations';
import { getErrorMessage } from '@/utils/errorUtils';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';

const passwordSchema = resetPasswordChangeSchema();

const ResetPasswordChange = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [resetPassword] = useResetPasswordMutation();
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirmation, setShowNewPasswordConfirmation] =
    useState(false);

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      if (!token) {
        setStatus('Reset token is missing or invalid.');
        setSubmitting(false);
        return;
      }

      try {
        setStatus(undefined);

        const response = await resetPassword({
          token,
          password: values.newPassword,
        }).unwrap();

        localStorage.removeItem('resetEmail');
        enqueueSnackbar(
          response.message || 'Your password has been updated successfully.',
          {
            variant: 'success',
          }
        );
        navigate('/auth/login', { replace: true });
      } catch (error) {
        const errorMessage = getErrorMessage(
          error,
          'Password reset failed. Please try again.'
        );

        setStatus(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="card max-w-[390px] w-full contrast:!bg-card">
      <form
        aria-label="Reset your password"
        className="card-body flex flex-col gap-5 p-10"
        onSubmit={formik.handleSubmit}
        noValidate
      >
        <div className="text-center">
          <h1 className="text-lg font-medium text-gray-900">Reset password</h1>
          <span className="text-2sm text-gray-700">
            Enter a new password for your admin account.
          </span>
        </div>

        {formik.status && <Alert variant="danger">{formik.status}</Alert>}

        <div className="flex flex-col gap-1">
          <label
            htmlFor="newPassword"
            className="form-label text-gray-900"
            aria-label="New Password"
          >
            New Password
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              placeholder="Enter a new password"
              autoComplete="new-password"
              {...formik.getFieldProps('newPassword')}
              className={clsx(
                'form-control input pr-10 !text-gray-900 placeholder:!text-gray-800',
                {
                  'is-invalid':
                    formik.touched.newPassword && formik.errors.newPassword,
                }
              )}
              onKeyDown={preventLeadingWhitespace}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-icon"
              onClick={() => setShowNewPassword(!showNewPassword)}
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
            >
              <KeenIcon
                icon="eye"
                className={clsx('text-gray-500', { hidden: showNewPassword })}
              />
              <KeenIcon
                icon="eye-slash"
                className={clsx('text-gray-500', { hidden: !showNewPassword })}
              />
            </button>
          </div>
          {formik.touched.newPassword && formik.errors.newPassword && (
            <span
              role="alert"
              className="text-danger contrast:text-red-500 text-xs mt-1"
            >
              {formik.errors.newPassword}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label
            htmlFor="confirmPassword"
            className="form-label font-normal text-gray-900"
            aria-label="Confirm New Password"
          >
            Confirm New Password
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showNewPasswordConfirmation ? 'text' : 'password'}
              placeholder="Re-enter your new password"
              autoComplete="new-password"
              {...formik.getFieldProps('confirmPassword')}
              className={clsx(
                'form-control input pr-10 !text-gray-900 placeholder:!text-gray-800',
                {
                  'is-invalid':
                    formik.touched.confirmPassword &&
                    formik.errors.confirmPassword,
                }
              )}
              onKeyDown={preventLeadingWhitespace}
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-icon"
              onClick={() =>
                setShowNewPasswordConfirmation(!showNewPasswordConfirmation)
              }
              aria-label={
                showNewPasswordConfirmation ? 'Hide password' : 'Show password'
              }
            >
              <KeenIcon
                icon="eye"
                className={clsx('text-gray-500', {
                  hidden: showNewPasswordConfirmation,
                })}
              />
              <KeenIcon
                icon="eye-slash"
                className={clsx('text-gray-500', {
                  hidden: !showNewPasswordConfirmation,
                })}
              />
            </button>
          </div>
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <span
              role="alert"
              className="text-danger contrast:text-red-500 text-xs mt-1"
            >
              {formik.errors.confirmPassword}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-5 items-stretch">
          <button
            type="submit"
            className="btn btn-primary flex justify-center grow"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? 'Updating password...' : 'Reset password'}
          </button>

          <Link
            to="/auth/login"
            className="flex items-center justify-center text-sm gap-2 text-gray-700 hover:text-primary"
          >
            <KeenIcon icon="black-left" />
            Back to Login
          </Link>
        </div>
      </form>
    </div>
  );
};

export { ResetPasswordChange };
